import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone, Layers, Zap, Award, Pen, Trophy, Sparkles, Briefcase, GraduationCap, Calendar, Heart, UtensilsCrossed, HardHat, Users } from "lucide-react";

const serviceLinks = [
  { label: "Screen Printing", slug: "screen-printing", icon: Layers, desc: "Bulk custom apparel from 12 pcs" },
  { label: "DTF Printing", slug: "dtf-printing", icon: Zap, desc: "No minimum, full-color printing" },
  { label: "Embroidery", slug: "embroidery", icon: Award, desc: "Premium stitched logos & text" },
  { label: "Graphic Design", slug: "graphic-design", icon: Pen, desc: "Print-ready artwork & logos" },
  { label: "Custom Jerseys", slug: "custom-jerseys", icon: Trophy, desc: "Team uniforms for all sports" },
  { label: "Specialty Printing", slug: "specialty-printing", icon: Sparkles, desc: "Puff, foil, metallic & glow" },
];

const industryLinks = [
  { label: "Corporate & Business", slug: "corporate", icon: Briefcase },
  { label: "Schools & Education", slug: "schools", icon: GraduationCap },
  { label: "Sports & Athletics", slug: "sports", icon: Trophy },
  { label: "Events & Occasions", slug: "events", icon: Calendar },
  { label: "Healthcare", slug: "healthcare", icon: Heart },
  { label: "Restaurants", slug: "restaurants", icon: UtensilsCrossed },
  { label: "Construction", slug: "construction", icon: HardHat },
  { label: "Church & Nonprofit", slug: "nonprofit", icon: Users },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [industriesOpen, setIndustriesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const servicesRef = useRef(null);
  const industriesRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
    setIndustriesOpen(false);
  }, [location]);

  const isActive = (path) => location.pathname.startsWith(path);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? "bg-black/95 border-b border-neutral-800 shadow-lg shadow-black/50" : "bg-black/80 border-b border-white/5"
      } backdrop-blur-md`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
            <img
              src="https://customer-assets.emergentagent.com/job_419da4a4-c2d8-43a0-81d2-8591121c3d6f/artifacts/vrml7tge_ninjascreenprintlogo.png"
              alt="Ninja Screen Print - Dallas, TX"
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {/* Services Dropdown */}
            <div
              ref={servicesRef}
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                data-testid="services-menu-btn"
                className={`flex items-center gap-1 px-3 py-2 text-sm font-inter font-medium uppercase tracking-wider transition-colors ${
                  isActive("/services") ? "text-red-500" : "text-neutral-300 hover:text-white"
                }`}
              >
                Services <ChevronDown size={14} className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
              </button>
              {servicesOpen && (
                <div
                  data-testid="services-mega-menu"
                  className="absolute top-full left-1/2 -translate-x-1/2 w-[560px] bg-[#0f0f0f] border border-neutral-800 shadow-2xl shadow-black/80 pt-4 pb-6 px-6 mega-menu"
                >
                  <div className="text-xs text-red-500 uppercase tracking-widest font-inter mb-4 pb-2 border-b border-neutral-800">
                    Our Services
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {serviceLinks.map((s) => (
                      <Link
                        key={s.slug}
                        to={`/services/${s.slug}`}
                        data-testid={`nav-service-${s.slug}`}
                        className="flex items-start gap-3 p-3 hover:bg-neutral-900 transition-colors group"
                      >
                        <s.icon size={16} className="text-red-500 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                        <div>
                          <div className="text-sm text-white font-medium group-hover:text-red-400 transition-colors">{s.label}</div>
                          <div className="text-xs text-neutral-500">{s.desc}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Industries Dropdown */}
            <div
              ref={industriesRef}
              className="relative"
              onMouseEnter={() => setIndustriesOpen(true)}
              onMouseLeave={() => setIndustriesOpen(false)}
            >
              <button
                data-testid="industries-menu-btn"
                className="flex items-center gap-1 px-3 py-2 text-sm font-inter font-medium text-neutral-300 hover:text-white uppercase tracking-wider transition-colors"
              >
                Industries <ChevronDown size={14} className={`transition-transform ${industriesOpen ? "rotate-180" : ""}`} />
              </button>
              {industriesOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-[400px] bg-[#0f0f0f] border border-neutral-800 shadow-2xl shadow-black/80 pt-4 pb-6 px-6 mega-menu">
                  <div className="text-xs text-red-500 uppercase tracking-widest font-inter mb-4 pb-2 border-b border-neutral-800">
                    Industries We Serve
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    {industryLinks.map((ind) => (
                      <Link
                        key={ind.slug}
                        to={`/industries/${ind.slug}`}
                        data-testid={`nav-industry-${ind.slug}`}
                        className="flex items-center gap-2 p-2 hover:bg-neutral-900 transition-colors group"
                      >
                        <ind.icon size={14} className="text-neutral-500 group-hover:text-red-500 transition-colors flex-shrink-0" strokeWidth={1.5} />
                        <span className="text-sm text-neutral-300 group-hover:text-white transition-colors">{ind.label}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/pricing"
              data-testid="nav-pricing"
              className={`px-3 py-2 text-sm font-inter font-medium uppercase tracking-wider transition-colors ${
                isActive("/pricing") ? "text-red-500" : "text-neutral-300 hover:text-white"
              }`}
            >
              Pricing
            </Link>
            <Link
              to="/blog"
              data-testid="nav-blog"
              className={`px-3 py-2 text-sm font-inter font-medium uppercase tracking-wider transition-colors ${
                isActive("/blog") ? "text-red-500" : "text-neutral-300 hover:text-white"
              }`}
            >
              Blog
            </Link>
            <Link
              to="/about"
              data-testid="nav-about"
              className={`px-3 py-2 text-sm font-inter font-medium uppercase tracking-wider transition-colors ${
                isActive("/about") ? "text-red-500" : "text-neutral-300 hover:text-white"
              }`}
            >
              About
            </Link>
            <Link
              to="/product-catalog"
              data-testid="nav-catalog"
              className={`px-3 py-2 text-sm font-inter font-medium uppercase tracking-wider transition-colors ${
                isActive("/product-catalog") ? "text-red-500" : "text-neutral-300 hover:text-white"
              }`}
            >
              Catalog
            </Link>
            <Link
              to="/team-store"
              data-testid="nav-team-store"
              className={`px-3 py-2 text-sm font-inter font-medium uppercase tracking-wider transition-colors ${
                isActive("/team-store") ? "text-red-500" : "text-neutral-300 hover:text-white"
              }`}
            >
              Team Store
            </Link>
          </div>

          {/* Right Side */}
          <div className="hidden lg:flex items-center gap-4">
            <a href="tel:+12145550190" data-testid="nav-phone" className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white transition-colors">
              <Phone size={14} className="text-red-500" />
              <span className="font-inter">(214) 555-0190</span>
            </a>
            <Link
              to="/contact"
              data-testid="nav-quote-btn"
              className="ninja-btn-primary text-sm py-2 px-6 font-teko tracking-widest text-base"
            >
              GET A QUOTE
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 text-neutral-300 hover:text-white"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#0f0f0f] border-t border-neutral-800 py-4">
          <div className="px-4 space-y-1">
            <div className="pb-2 mb-2 border-b border-neutral-800">
              <div className="text-xs text-red-500 uppercase tracking-widest mb-2 font-inter">Services</div>
              {serviceLinks.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  data-testid={`mobile-service-${s.slug}`}
                  className="flex items-center gap-2 py-2 text-neutral-300 hover:text-white text-sm"
                >
                  <s.icon size={14} className="text-red-500" strokeWidth={1.5} />
                  {s.label}
                </Link>
              ))}
            </div>
            <div className="pb-2 mb-2 border-b border-neutral-800">
              <div className="text-xs text-red-500 uppercase tracking-widest mb-2 font-inter">Industries</div>
              {industryLinks.map((ind) => (
                <Link
                  key={ind.slug}
                  to={`/industries/${ind.slug}`}
                  data-testid={`mobile-industry-${ind.slug}`}
                  className="flex items-center gap-2 py-2 text-neutral-300 hover:text-white text-sm"
                >
                  <ind.icon size={14} className="text-red-500" strokeWidth={1.5} />
                  {ind.label}
                </Link>
              ))}
            </div>
            {[
              { to: "/pricing", label: "Pricing" },
              { to: "/product-catalog", label: "Catalog" },
              { to: "/team-store", label: "Team Store" },
              { to: "/blog", label: "Blog" },
              { to: "/about", label: "About" },
              { to: "/service-area", label: "Service Area" },
            ].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="block py-2 text-neutral-300 hover:text-white text-sm uppercase tracking-wider"
              >
                {item.label}
              </Link>
            ))}
            <div className="pt-3 border-t border-neutral-800 mt-3">
              <a href="tel:+12145550190" className="flex items-center gap-2 text-sm text-neutral-400 mb-3">
                <Phone size={14} className="text-red-500" />
                (214) 555-0190
              </a>
              <Link
                to="/contact"
                data-testid="mobile-quote-btn"
                className="block text-center ninja-btn-primary py-3 text-base font-teko tracking-widest"
              >
                GET A QUOTE
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
